/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './telemetry-overview.scss';

/* eslint-disable import/no-unresolved, import/default */

import pageTemplate from './telemetry-overview.tpl.html';

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.telemetryOverview', [])
    .directive('agTelemetryOverview', TelemetryOverview)
    .name;

/*@ngInject*/
function TelemetryOverview() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            isSiloGroup: '=',
            isDashboard: '=',
            providedAssetId: '=',
            telemetryType: '<'
        },
        controller: TelemetryOverviewController,
        controllerAs: 'vm',
        templateUrl: pageTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function TelemetryOverviewController($scope, $filter, $translate, types, agTypes, agUtils, siloService, siloGroupService, unitService, userService) {

    let vm = this;
    vm.resolveIcon = resolveIcon;
    vm.resolveWidgetType = resolveWidgetType;
    vm.agTypes = agTypes;
    vm.loading = false;

    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.assetId = null;
    vm.silo = null;
    vm.siloGroup = null;

    const telemetryConstants = vm.isSiloGroup ? agTypes.telemetry.siloGroup : agTypes.telemetry.silo;
    initTelemetryTypeSpecificSettings();

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };

    $scope.$watch('vm.telemetryType', function() {
        initTelemetryTypeSpecificSettings();

        if (vm.assetId) {
            loadAsset();
        }
    });

    $scope.$watch('vm.ctx', function() {
        if (vm.ctx && vm.showData) {
            vm.ctx.widgetActions = [vm.helpPageAction];
            vm.ctx.hideTitlePanel = false;

            vm.assetId = null;

            if (vm.providedAssetId) {
                vm.assetId = vm.providedAssetId;
            } else {
                if (vm.ctx.stateController) {
                    vm.assetId = vm.ctx.stateController.getStateParams().entityId;
                }
                $scope.$watch(function () {
                        return vm.ctx.stateController.getStateParams().entityId;
                    },
                    function (newAssetId, prevAssetId) {
                        if (newAssetId && !angular.equals(newAssetId, prevAssetId)) {
                            vm.assetId = newAssetId;
                            loadAsset();
                        }
                    }
                );
            }

            if (vm.assetId) {
                loadAsset();
            }
        }
    });

    function resolveWidgetType() {
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            return agTypes.siloWidgetType.siloTemperatureDetails;
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            return agTypes.siloWidgetType.siloMoistureDetails;
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            return agTypes.siloWidgetType.siloCO2Details;
        }

        return null;
    }

    function initTelemetryTypeSpecificSettings() {
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {

            vm.telemetryKeys = [telemetryConstants.currentMinTemperature, telemetryConstants.currentAvgTemperature, telemetryConstants.currentMaxTemperature];
            vm.telemetryDeltaKeys = [telemetryConstants.currentMinDeltaTemperature, telemetryConstants.currentAvgDeltaTemperature, telemetryConstants.currentMaxDeltaTemperature];
            vm.telemetryLabel = $translate.instant('ag.temperature.temperature');
            vm.deltaTelemetryLabel = $translate.instant('ag.temperature.delta-temperature');
            vm.telemetrySecondaryLabel = $translate.instant('ag.temperature.temperature') + ` - ${unitService.temperatureUnitSymbol()}`;
            vm.deltaTelemetrySecondaryLabel = $translate.instant('ag.temperature.delta-temperature') + ` - ${unitService.temperatureUnitSymbol()}`;
            vm.historyUnits = unitService.temperatureUnitSymbol();

        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {

            vm.telemetryKeys = [telemetryConstants.currentMinMoisture, telemetryConstants.currentAvgMoisture, telemetryConstants.currentMaxMoisture];
            vm.telemetryDeltaKeys = [telemetryConstants.currentMinDeltaMoisture, telemetryConstants.currentAvgDeltaMoisture, telemetryConstants.currentMaxDeltaMoisture];
            vm.telemetryLabel = $translate.instant('ag.moisture.moisture');
            vm.deltaTelemetryLabel = $translate.instant('ag.moisture.delta-moisture');
            vm.telemetrySecondaryLabel = $translate.instant('ag.moisture.moisture') + ` - ${unitService.moistureUnitSymbol()}`;
            vm.deltaTelemetrySecondaryLabel = $translate.instant('ag.moisture.delta-moisture') + ` - ${unitService.moistureUnitSymbol()}`;
            vm.historyUnits = unitService.moistureUnitSymbol();
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {

            vm.telemetryKeys = [telemetryConstants.currentMinCO2, telemetryConstants.currentAvgCO2, telemetryConstants.currentMaxCO2];
            vm.telemetryDeltaKeys = [telemetryConstants.currentMinDeltaCO2, telemetryConstants.currentAvgDeltaCO2, telemetryConstants.currentMaxDeltaCO2];
            vm.telemetryLabel = $translate.instant('ag.co2.co2');
            vm.deltaTelemetryLabel = $translate.instant('ag.co2.delta-co2');
            vm.telemetrySecondaryLabel = $translate.instant('ag.co2.co2') + ` - ${unitService.co2UnitSymbol()}`;
            vm.deltaTelemetrySecondaryLabel = $translate.instant('ag.co2.delta-co2') + ` - ${unitService.co2UnitSymbol()}`;
            vm.historyUnits = unitService.co2UnitSymbol();
        }
    }

    $scope.$watch(function() { return vm.silo ? (vm.silo.label || vm.silo.name) : null; }, function(title) {
        if (vm.showData && vm.ctx && title) {
            vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-overview') + ': ' + title;
        }
    });

    $scope.$watch(function() { return vm.siloGroup ? (vm.siloGroup.label || vm.siloGroup.name) : null; }, function(title) {
        if (vm.showData && vm.ctx && vm.isSiloGroup && title) {
            vm.ctx.widgetTitle = $translate.instant('ag.silo-group.silo-group-overview') + ': ' + title;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
    });

    function loadAsset() {
        vm.silo = null;
        vm.siloGroup = null;
        reset();

        if (vm.assetId) {
            vm.loading = true;

            if (vm.isSiloGroup) {
                loadSiloGroupData();
            } else {
                loadSiloData();
            }
        }
    }

    function loadSiloData() {
        siloService.getSilo(vm.assetId).then(
            function success(silo) {
                vm.silo = silo;

                if (vm.telemetryType === agTypes.telemetry.type.temperature) {
                    vm.maxTelemetry = vm.silo.maxTemperature || 24;
                    vm.maxDeltaTelemetry = vm.silo.maxDeltaTemperature || 1;
                } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
                    vm.maxTelemetry = vm.silo.maxMoisture || 24;
                    vm.maxDeltaTelemetry = vm.silo.maxDeltaMoisture || 1;
                } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
                    vm.maxTelemetry = vm.silo.maxCO2 || 24;
                    vm.maxDeltaTelemetry = vm.silo.maxDeltaCO2 || 1;
                }

                updateChartSubscription();

                vm.loading = false;
            },
            function fail() {
                vm.loading = false;
            }
        );
    }

    function loadSiloGroupData() {

        siloGroupService.getSiloGroup(vm.assetId).then(
            function success(siloGroup) {
                vm.siloGroup = siloGroup;

                siloGroupService.getSiloGroupSilos(vm.assetId).then(
                    (silos) => {
                        vm.maxTelemetry = null;
                        vm.maxDeltaTelemetry = null;

                        silos.forEach((silo) => {
                            let maxTelemetry = silo.maxTemperature;
                            let maxDeltaTelemetry = silo.maxDeltaTemperature;

                            if (vm.telemetryType === agTypes.telemetry.type.moisture) {
                                maxTelemetry = silo.maxMoisture;
                                maxDeltaTelemetry = silo.maxDeltaMoisture;
                            } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
                                maxTelemetry = silo.maxCO2;
                                maxDeltaTelemetry = silo.maxDeltaCO2;
                            }

                            if (angular.isDefined(maxTelemetry) && maxTelemetry != null) {
                                if (vm.maxTelemetry == null) {
                                    vm.maxTelemetry = maxTelemetry;
                                } else {
                                    vm.maxTelemetry = Math.min(vm.maxTelemetry, maxTelemetry);
                                }
                            }

                            if (angular.isDefined(maxDeltaTelemetry) && maxDeltaTelemetry != null) {
                                if (vm.maxDeltaTelemetry == null) {
                                    vm.maxDeltaTelemetry = maxDeltaTelemetry;
                                } else {
                                    vm.maxDeltaTelemetry = Math.min(vm.maxDeltaTelemetry, maxDeltaTelemetry);
                                }
                            }
                        });

                        vm.maxTelemetry = vm.maxTelemetry || 24;
                        vm.maxDeltaTelemetry = vm.maxDeltaTelemetry || 1;

                        updateChartSubscription();

                        vm.loading = false;
                    },
                    () => {
                        vm.loading = false;
                    }
                );
            },
            function fail() {
                vm.loading = false;
            }
        );
    }

    function updateChartSubscription() {
        vm.chartSubscriptionsInfo = null;

        let entityId;
        let entityName;
        if (vm.isSiloGroup) {
            entityId = vm.siloGroup.id.id;
            entityName = vm.siloGroup.name;
        } else {
            entityId = vm.silo.id.id;
            entityName = vm.silo.name;
        }

        let assetConstants = vm.isSiloGroup ? agTypes.telemetry.siloGroup : agTypes.telemetry.silo;
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            vm.chartSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.asset,
                entityId: entityId,
                entityName: entityName,
                timeseries: [
                    {
                        name: assetConstants.currentMaxTemperature,
                        label: $translate.instant('ag.silo.max-temperature'),
                        color: agTypes.colors.maxColor,
                        postFuncBody: unitService.temperaturePostprocessingFuncBody()
                    }
                ]
            }];
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            vm.chartSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.asset,
                entityId: entityId,
                entityName: entityName,
                timeseries: [
                    {
                        name: assetConstants.currentMaxMoisture,
                        label: $translate.instant('ag.silo.max-moisture'),
                        color: agTypes.colors.minColor
                    }
                ]
            }];
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            vm.chartSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.asset,
                entityId: entityId,
                entityName: entityName,
                timeseries: [
                    {
                        name: assetConstants.currentMaxCO2,
                        label: $translate.instant('ag.silo.max-co2'),
                        color: agTypes.colors.minColor
                    }
                ]
            }];
        }
    }

    function resolveIcon() {
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            return 'ag:thermometer';
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            return 'ag:moisture';
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            return 'ag:co2_level';
        } else {
            return '';
        }
    }

    function reset() { }
}
